import { Scrollable } from "@/components/Scrollable/Scrollable";
import { BasicWriter } from "@/components/Writer/BasicWriter";
import { Page, PageHeader } from "@/components/app-layout";
import { AppTopRightMenu } from "@/components/app-layout/AppTopRightMenu";
import { PageBody } from "@/components/app-layout/PageBody";
import { GroupOrUnknownDisplayName } from "@/components/display/GroupDisplayName";
import { UserOrUnknownDisplayName } from "@/components/display/UserDisplayName";
import { DatesDisplay } from "@/components/display/attributes/DatesDisplay";
import { Markdown } from "@/components/markdown/Markdown";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { Column, Row } from "@/components/ui/flex";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useExpertEditData } from "@/data/use-expert-edit-data";
import {
  usePerspectiveData,
  usePerspectivesData,
} from "@/data/use-perspectives-data";
import { useGroupTemplatesData } from "@/data/use-templates-data";
import { useGroupSlug } from "@/hooks/use-group-slug";
import { useGroupUuid } from "@/hooks/use-group-uuid";
import { useKitUuid } from "@/hooks/use-kit-uuid";
import type { GetKitExpertsResponseDto } from "@/lib/gen-api";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTrash } from "@nnr/fontawesome-sharp-light/icons/sharp/light";
import { Link, useNavigate, useParams } from "@tanstack/react-router";
import { useLayoutEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { useDebounceCallback, useResizeObserver } from "usehooks-ts";

export default function PerspectivePage() {
  const groupUuid = useGroupUuid();
  const kitUuid = useKitUuid();
  const { expert } = useExpertEditData({ groupUuid, kitUuid });

  return (
    <Page document>{expert && <PerspectiveContent expert={expert} />}</Page>
  );
}

interface PerspectiveContentProps {
  expert: GetKitExpertsResponseDto["data"][number];
}

function PerspectiveContent({ expert }: PerspectiveContentProps) {
  const expertUuid = expert.uuid;
  const groupUuid = useGroupUuid();
  const groupSlug = useGroupSlug();
  const kitUuid = useKitUuid();
  const perspectiveUuid =
    (useParams({ strict: false }) as Record<string, string | undefined>)
      .perspectiveUuid || null;
  const { perspectives, deletePerspective } = usePerspectivesData(
    groupUuid,
    kitUuid,
    expertUuid,
  );
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [activeTab, setActiveTab] = useState<"scaffolding" | "instructions">(
    "scaffolding",
  );
  const [showFullDescription, setShowFullDescription] = useState(false);
  const [isDescriptionTruncated, setIsDescriptionTruncated] = useState(false);
  const descriptionRef = useRef<HTMLSpanElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const { width: containerWidth = 0 } = useResizeObserver({
    ref: containerRef,
  });
  const isNarrow = containerWidth > 0 && containerWidth < 600;
  const navigate = useNavigate();

  const currentPerspective = perspectives.find(
    (s) => s.uuid === perspectiveUuid,
  );

  const { templates } = useGroupTemplatesData({
    groupUuid,
  });
  const template =
    templates.find((t) => t.uuid === currentPerspective?.template_uuid) ?? null;
  const isSameGroup = template?.group?.uuid === groupUuid;

  useLayoutEffect(() => {
    const el = descriptionRef.current;
    if (el) {
      setIsDescriptionTruncated(el.scrollHeight > el.clientHeight);
    }
  }, [template?.description]);

  async function handleDelete() {
    if (!currentPerspective) {
      return;
    }

    await deletePerspective(currentPerspective.uuid);

    toast.success("Perspective deleted.");

    const remainingPerspectives = perspectives.filter(
      (s) => s.uuid !== currentPerspective.uuid,
    );

    if (remainingPerspectives.length > 0) {
      navigate({
        to: "/w/$groupSlug/k/$kitUuid/perspectives/$perspectiveUuid",
        params: {
          groupSlug,
          kitUuid: kitUuid!,
          perspectiveUuid: remainingPerspectives[0].uuid,
        },
        replace: true,
      });
    } else {
      navigate({
        to: "/w/$groupSlug/k/$kitUuid",
        params: { groupSlug, kitUuid: kitUuid! },
        replace: true,
      });
    }
  }

  if (!currentPerspective) {
    return null;
  }

  return (
    <>
      <AppTopRightMenu.ContentPortal>
        <Button
          size="icon"
          variant="outline"
          onClick={() => setShowDeleteDialog(true)}
        >
          <FontAwesomeIcon icon={faTrash} />
        </Button>
      </AppTopRightMenu.ContentPortal>
      <PageHeader>
        <PageHeader.Title>
          <Tooltip>
            <TooltipTrigger asChild>
              <span className={isNarrow ? "line-clamp-1" : ""}>
                {currentPerspective.title}
              </span>
            </TooltipTrigger>
            {isNarrow && (
              <TooltipContent>{currentPerspective.title}</TooltipContent>
            )}
          </Tooltip>
        </PageHeader.Title>
      </PageHeader>
      <PageBody document innerClassName="overflow-hidden">
        <PageBody.Metadata
          className={isNarrow ? "flex-col" : "items-start justify-between"}
        >
          <Column align="start" className="gap-2">
            {template && (
              <>
                <PageBody.MetadataGroup>
                  {isSameGroup && template.kitUuid ? (
                    <Link
                      to="/w/$groupSlug/k/$kitUuid/template"
                      params={{ groupSlug, kitUuid: template.kitUuid }}
                      className="hover:underline"
                    >
                      Template: {template.name}
                    </Link>
                  ) : (
                    <span className="flex items-center gap-1">
                      Template: {template.name}
                      {template.group && (
                        <>
                          {" "}
                          (Shared by{" "}
                          <GroupOrUnknownDisplayName group={template.group} />)
                        </>
                      )}
                    </span>
                  )}
                </PageBody.MetadataGroup>
                <PageBody.MetadataGroup className="flex-col items-start">
                  {template.description && (
                    <Collapsible
                      open={showFullDescription}
                      onOpenChange={setShowFullDescription}
                      className="group flex flex-col items-start gap-1"
                    >
                      <span
                        ref={descriptionRef}
                        className="line-clamp-2 group-data-[state=open]:hidden"
                      >
                        {template.description}
                      </span>
                      <CollapsibleContent>
                        {template.description}
                      </CollapsibleContent>
                      {isDescriptionTruncated && (
                        <CollapsibleTrigger className="hover:underline">
                          {showFullDescription ? "View less" : "View more"}
                        </CollapsibleTrigger>
                      )}
                    </Collapsible>
                  )}
                </PageBody.MetadataGroup>
              </>
            )}
          </Column>
          <PageBody.MetadataGroup
            className={isNarrow ? "self-start" : "shrink-0 self-center"}
          >
            <DatesDisplay
              dateCreated={currentPerspective.dateCreated}
              dateUpdated={currentPerspective.dateUpdated}
            />
            <UserOrUnknownDisplayName user={expert.user} />
            <GroupOrUnknownDisplayName group={expert.group} />
          </PageBody.MetadataGroup>
        </PageBody.Metadata>
        <div
          ref={containerRef}
          className="@container flex flex-1 overflow-hidden"
        >
          <div className="flex flex-1 flex-col gap-8 overflow-hidden @[600px]:flex-row">
            {template && (
              <Column scrollableParent className="gap-4 py-1">
                <Row align="center" className="gap-3">
                  <h2 className="text-foreground-5 shrink-0 text-lg font-bold">
                    Template:
                  </h2>
                  <Tabs
                    value={activeTab}
                    onValueChange={(v) =>
                      setActiveTab(v as "scaffolding" | "instructions")
                    }
                  >
                    <TabsList>
                      <TabsTrigger
                        value="scaffolding"
                        className="h-6 px-2 text-xs"
                      >
                        Starting Skeleton
                      </TabsTrigger>
                      <TabsTrigger
                        value="instructions"
                        className="h-6 px-2 text-xs"
                      >
                        AI Instructions
                      </TabsTrigger>
                    </TabsList>
                  </Tabs>
                </Row>
                <Separator />
                <Scrollable outerClassName="flex-1">
                  <Markdown allowRawHtml className="prose prose-sm max-w-none">
                    {activeTab === "scaffolding"
                      ? template.scaffolding
                      : template.instructions}
                  </Markdown>
                </Scrollable>
              </Column>
            )}
            <Column scrollableParent className="gap-4 py-1">
              <h2 className="text-foreground-5 line-clamp-1 text-lg font-bold">
                {expert.name} Perspective
              </h2>
              <Separator />
              <PerspectiveEditor
                groupUuid={groupUuid}
                kitUuid={kitUuid}
                expertUuid={expertUuid}
                perspectiveUuid={currentPerspective.uuid}
              />
            </Column>
          </div>
        </div>
      </PageBody>

      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Delete {currentPerspective.title}
            </AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete the
              document and remove all associated data.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

interface PerspectiveEditorProps {
  groupUuid: string | null;
  kitUuid: string | null;
  expertUuid: string;
  perspectiveUuid: string;
}

const PerspectiveEditor = ({
  groupUuid,
  kitUuid,
  expertUuid,
  perspectiveUuid,
}: PerspectiveEditorProps) => {
  const { perspective, updatePerspective } = usePerspectiveData(
    groupUuid,
    kitUuid,
    expertUuid,
    perspectiveUuid,
  );

  const handleCreateInstructionsUpdate = (instructions: string) => {
    updatePerspective({ instructions });
  };

  const onChange = useDebounceCallback(
    (instructions: string) => {
      updatePerspective({ instructions });
    },
    1000,
    { maxWait: 5000 },
  );

  const editorContainerRef = useRef<HTMLDivElement>(null);

  const focusEditor = () => {
    editorContainerRef.current
      ?.querySelector<HTMLElement>(".ProseMirror")
      ?.focus();
  };

  if (!perspective) {
    return null;
  }

  return (
    <Column
      ref={editorContainerRef}
      onClick={focusEditor}
      scrollableParent
      className="cursor-text"
    >
      <BasicWriter
        content={perspective.instructions}
        onUpdate={handleCreateInstructionsUpdate}
        onChange={onChange}
        placeholder="Enter create instructions..."
        hasToolbar={false}
        deps={[perspectiveUuid]}
      />
    </Column>
  );
};
